import { TWordSearchFilters } from 'shared/types/domain';

import { calcNextProbe, TRepetitionAction } from 'core/helpers/repetition';

import {
  ACCESS_TOKEN,
  ACCESS_TOKEN_STORAGE,
  API_PREFIX,
  apiLog,
  doDeleteQuery,
  doGetQuery,
  doPostQuery,
  doPutQuery,
} from './utils';

// use ?at={accessToken} as a query param for <audio/> in case of local dev env.
const accessQueryParams =
  ACCESS_TOKEN_STORAGE === 'localStorage' ? `?at=${ACCESS_TOKEN}` : '';

const toWordDTO = (word: TWordUpdate): TWordUpdate => ({
  value: word.value,
  definitions: Object.fromEntries(
    Object.entries(word.definitions).filter(([, v]) => !!v)
  ),
  note: word.note,
  valueNote: word.valueNote,
  image: word.image,
  tags: word.tags,
  valueResourceBinary: word.valueResourceBinary,
  definitionResourceBinary: word.definitionResourceBinary,
});

export const apiAddWord = async (
  deckUid: string,
  word: TWordUpdate
): Promise<string> => {
  const { wordUid } = await doPostQuery<{ wordUid: string }>(
    `words/word/${deckUid}`,
    null,
    { word: toWordDTO(word) }
  );
  return wordUid;
};

export const apiUpdateWord = async (wordUid: string, word: TWordUpdate) => {
  await doPutQuery(`words/word/${wordUid}`, null, { word: toWordDTO(word) });
};

export const apiGetWord = async (wordUid: string): Promise<TWord> =>
  await doGetQuery<TWord>(`words/word/${wordUid}`);

export const apiGetWordWithDeck = async (
  wordUid: string
): Promise<{ word: TWord; deck: TDeck }> =>
  await doGetQuery<{ word: TWord; deck: TDeck }>(
    `words/word-with-deck/${wordUid}`
  );

export const apiFinishWord = async (
  wordUid: string,
  exercise: TExercise,
  action: TRepetitionAction
): Promise<TUndo> => {
  const word = orDie(await apiGetWord(wordUid));

  const nextProbeAt = calcNextProbe(exercise, word, action);
  await doPostQuery(`words/finish/${wordUid}`, {
    exercise,
    action: action.type,
    nextProbeAt,
  });
  apiLog(`Word ${word.uid} has been updated`);

  return { word, exercise };
};

export const apiSetWordStatus = async (
  wordUid: string,
  exercise: TExercise,
  status: TExerciseStatus | undefined
): Promise<TWord> =>
  doPostQuery<TWord>(
    `words/setWordStatus/${wordUid}`,
    { exercise },
    { status }
  );

export const apiUndoFinishWord = async (undo: TUndo) => {
  const { word, exercise } = undo;
  const status = undo.word.status[undo.exercise];

  const updWord = await apiSetWordStatus(word.uid, exercise, status);
  apiLog(`The progress of ${exercise} for ${word.uid} was rollbacked`);
  return updWord;
};

export const apiGetClosestPendingTime = async (
  deckUid: string,
  exercise: TExercise
): Promise<string | null> => {
  const { time } = await doGetQuery<{ time: string | null }>(
    `words/closest-pending/${deckUid}/${exercise}`
  );
  return time;
};

export const apiGetPendingWords = async (
  deckUid: string,
  exercise: TExercise
): Promise<TWord[]> =>
  await doGetQuery<TWord[]>(`words/pending/${deckUid}/${exercise}`);

export const apiDeleteWord = async (wordUid: string) =>
  await doDeleteQuery<void>(`words/word/${wordUid}`);

export const apiMoveWordToDeck = async (wordUid: string, deckUid: string) =>
  await doPostQuery<void>(`words/word/move/${wordUid}/${deckUid}`);

export const apiGetSpeechAudio = (wordUid: string): string =>
  `${API_PREFIX}words/synthesize/${wordUid}` + accessQueryParams;

export const apiGetNumberSpeechAudio = (lang: TLang, number: number): string =>
  `${API_PREFIX}words/synthesize-number/${lang}/${number}` + accessQueryParams;

export const apiGetAllWords = () => doGetQuery<TWord[]>(`words/list`);

export const apiSearchWords = (
  deckUid: string,
  cursor: string | undefined,
  filters: TWordSearchFilters
) =>
  doGetQuery<{ words: TWord[]; cursor: string | undefined }>(`words/search`, {
    deckUid,
    cursor,
    text: filters.text,
    sort: filters.sort,
  });

export const apiGetPendingGraph = async (
  deckUid: string,
  exercise: TExercise
): Promise<{ count: number; weeks: number }[]> =>
  await doGetQuery<{ count: number; weeks: number }[]>(
    `words/pendingGraph/${deckUid}/${exercise}`
  );
